import * as React from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import "yet-another-react-lightbox/styles.css";
import LandingSection from "../components/LandingSection";
import Footer from "../components/Footer";
import ActionButton from "../utils/ActionButton";
import RotateLeftIcon from "@mui/icons-material/RotateLeft";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function Gallery() {
  // Dynamically generate itemData
  const itemData = Array.from({ length: 50 }, (_, i) => ({
    img: `https://utahdata.org/sumit/1 (${i + 1}).webp`,
    title: `Utah Image ${i + 1}`,
  }));

  const [open, setOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [rotations, setRotations] = useState(Array(itemData.length).fill(0));

  const [activeIndex, setActiveIndex] = useState(0);

  const nextSlide = () =>
    setActiveIndex((prev) => (prev + 1) % itemData.length);
  const prevSlide = () =>
    setActiveIndex((prev) => (prev - 1 + itemData.length) % itemData.length);

  // Symmetric 3-left & 3-right positioning
  const getOffset = (index) => {
    const total = itemData.length;
    let diff = index - activeIndex;

    // Circular wrapping
    if (diff < -total / 2) diff += total;
    if (diff > total / 2) diff -= total;

    return diff;
  };

  const getPosition = (offset) => {
    const abs = Math.abs(offset);

    if (abs > 3) return { scale: 0, x: 0, zIndex: 0, rotateY: 0 };

    const scale = 1.2 - abs * 0.1;
    const x = offset * 220;
    // const opacity = 1 - abs * 0.2;
    const zIndex = 50 - abs * 10;
    const rotateY = offset * -10;

    return { scale, x, zIndex, rotateY };
  };

  const handleRotate = (direction) => {
    setRotations((prev) => {
      const updated = [...prev];
      updated[currentIndex] =
        (updated[currentIndex] + (direction === "right" ? 90 : -90)) % 360;
      return updated;
    });
  };

  const resetRotation = () => {
    setRotations((prev) => {
      const updated = [...prev];
      updated[currentIndex] = 0;
      return updated;
    });
  };

  return (
    <div>
      <LandingSection
        data={{
          imageName: "utah-gallery.webp",
          title: "Utah Gallery",
          visible: "true",
        }}
      />

      <div className="verticals_section">
        <div className="verticals-intro">
          <div className="verticals-intro-sec1">
            <h2>Discover Utah Through Images.</h2>
          </div>
          <div className="verticals-intro-sec2">
            <p>
              Experience the breathtaking beauty of Utah through the lens of{" "}
              <a
                href="https://www.linkedin.com/in/sumitparashar/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Sumit Parashar
              </a>
              . Each photograph in this gallery captures a unique moment,
              showcasing the landscapes, textures, and spirit of the Utah State
              as seen by his keen eye and deep appreciation for nature.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 text-center mb-0 p-10">
        <p>Utah Gallery</p>
        <h3>Utah's Visual Diary</h3>
        <p className="m-0 text-[12px]">
          See the Utah through{" "}
          <a
            href="https://www.linkedin.com/in/sumitparashar/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Sumit Parashar
          </a>{" "}
          lens:
        </p>
      </div>

      <div className="bg-gray-50 flex items-center justify-center">
        <div className="relative w-full flex flex-col items-center py-10 overflow-hidden bg-gray-50">
          <div className="relative w-full flex justify-center perspective-[1200px] h-[420px]">
            {itemData.map((item, i) => {
              const offset = getOffset(i);
              const { scale, x, zIndex, rotateY } = getPosition(offset);

              return (
                <motion.div
                  key={i}
                  initial={false}
                  animate={{ scale, x, rotateY }}
                  transition={{ duration: 0.7, ease: "easeInOut" }}
                  style={{ zIndex }}
                  className="absolute shadow-2xl rounded-2xl overflow-hidden"
                >
                  <img
                    src={item.img}
                    onClick={() => {
                      setCurrentIndex(i);
                      setOpen(true);
                    }}
                    alt={`slide-${i}`}
                    className="w-[400px] h-[400px] object-cover rounded-2xl b cursor-pointer"
                  />
                </motion.div>
              );
            })}
          </div>

          <div className="flex gap-6 mt-10">
            <div
              onClick={prevSlide}
              className="w-10 h-10 rounded-full bg-slate-200 shadow-md flex items-center justify-center hover:bg-gray-300 transition cursor-pointer"
            >
              <ChevronLeft />
            </div>
            <div
              onClick={nextSlide}
              className="w-10 h-10 rounded-full bg-slate-200 shadow-md flex items-center justify-center hover:bg-gray-300 transition cursor-pointer"
            >
              <ChevronRight />
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="lightbox-container">
          <Lightbox
            open={open}
            close={() => setOpen(false)}
            index={currentIndex}
            slides={itemData.map((item, index) => ({
              src: item.img,
              style: { transform: `rotate(${rotations[index]}deg)` },
            }))}
            plugins={[Zoom]}
            zoom={{
              maxZoomPixelRatio: 5,
              zoomInMultiplier: 2,
              doubleTapDelay: 300,
              doubleClickDelay: 500,
              scrollToZoom: false,
            }}
            controller={{
              closeOnBackdropClick: true,
              closeOnPullDown: true,
            }}
            carousel={{
              finite: false,
              preload: 2,
            }}
            render={{
              // Add Prev and Next buttons
              buttonPrev: () => (
                <button
                  className="yarl__button yarl__button_prev"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex(
                      (prev) => (prev - 1 + itemData.length) % itemData.length
                    );
                  }}
                  aria-label="Previous"
                  style={{
                    position: "absolute",
                    left: "100px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 100,
                    background: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "45px",
                    height: "45px",
                    color: "#000",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  <ChevronLeft size={28} />
                </button>
              ),
              buttonNext: () => (
                <button
                  className="yarl__button yarl__button_next"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentIndex((prev) => (prev + 1) % itemData.length);
                  }}
                  aria-label="Next"
                  style={{
                    position: "absolute",
                    right: "100px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 100,
                    background: "#fff",
                    border: "none",
                    borderRadius: "50%",
                    width: "45px",
                    height: "45px",
                    color: "#000",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "20px",
                  }}
                >
                  <ChevronRight size={28} />
                </button>
              ),

              // Keep your rotate buttons in toolbar
              toolbar: ({ toolbar: { buttons, ...rest } }) => ({
                buttons: [
                  <button
                    key="rotate-left"
                    className="yarl__button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRotate("left");
                    }}
                    aria-label="Rotate Left"
                  >
                    <RotateLeftIcon />
                  </button>,
                  <button
                    key="rotate-right"
                    className="yarl__button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRotate("right");
                    }}
                    aria-label="Rotate Right"
                  >
                    <RotateRightIcon />
                  </button>,
                  ...buttons,
                ],
                ...rest,
              }),
            }}
          />
        </div>
      )}

      <Footer />
    </div>
  );
}
